// src/oura/components/search/SearchesAboutYou.tsx
//
// The searches customers make about this business (#3879).
//
// The keyword table on this screen is what Google already sends traffic for.
// This is the question the Visibility page answers for AI, asked of Google: is
// the search covered by one of the 50, and does the site answer it?
//
// Every row says "Covered by Q41" or "Not in your 50", so the two pages can
// never disagree about the same search.
//
// The footer is not decoration. Search Console cannot report a search the site
// has never appeared for, so a reader who does not know that will read this
// card as the whole market.

import { useMemo, useState } from "react";
import { UsersIcon } from "lucide-react";
import type { AeoSearchQuestions } from "@service/aeo";
import { cn } from "../../lib/cn";
import { judgedSearches, perYear, questionSlot, SEARCHES_NOT_BUILT_SENTENCE, searchesAbsentSentence, searchesNotBuilt } from "../../topSearches";
import { useActionDrawer } from "../ActionDrawer";
import { useOuraRole } from "../../role";
import type { QuestionResult } from "../../contract";
import type { StageRefreshJob } from "../../hooks/useRefreshJob";
import { CardRefresh, RefreshProgress } from "../RefreshControl";
import { SEARCH_REFRESH } from "../../refreshJobs";
import { FOCUS_RING, FoldCard, RankChip, Seg, TYPE, Tag } from "@oura/ui";
import { EMPTY_CELL, TABLE } from "../client/staffScreen";

type Filter = "unanswered" | "answered" | "unjudged" | "all";

const TITLE = "Searches customers make about you";
const ICON = { icon: UsersIcon, area: "google" } as const;
const FOOTNOTE = "Search Console only records searches the site already appears for. The Visibility page covers the ones it does not.";
const CLIENT_FOOTNOTE = "Google only lists searches your site already shows up for. The Visibility page covers the rest.";

export function SearchesAboutYou({
    block,
    questions,
    searchJob,
}: {
    block?: AeoSearchQuestions | null;
    questions?: QuestionResult[];
    /** The shared Search Console pull (#3907). Absent means no control. */
    searchJob?: StageRefreshJob;
}) {
    const [filter, setFilter] = useState<Filter>("unanswered");
    /**
     * The same fix, opened the same way (#3879).
     *
     * The Visibility table's "View fix" calls `openAction(q.actionId)` on the
     * shared drawer, and so does this one: there is no second fix flow. A
     * search reaches its action through the question covering it; a row the 50
     * do not cover has no fix yet, so it shows no button rather than a dead
     * one. Staff only, matching the table this mirrors.
     */
    const { openAction } = useActionDrawer();
    const isStaff = useOuraRole() === "oura-staff";
    const footnote = isStaff ? FOOTNOTE : CLIENT_FOOTNOTE;
    const fixBySlot = useMemo(() => {
        const m = new Map<number, string>();
        (questions ?? []).forEach((q, i) => {
            const n = questionSlot(q as { n?: number; slot_n?: number }, i);
            if (n !== null && q.actionId) m.set(n, q.actionId);
        });
        return m;
    }, [questions]);
    const fixFor = (coveredBy: number | null) =>
        (isStaff && coveredBy !== null ? fixBySlot.get(coveredBy) ?? null : null);
    const rows = useMemo(() => judgedSearches(block), [block]);
    const noSearchesReason = useMemo(() => searchesAbsentSentence(block), [block]);
    /** The backend's sentence for an absent reason, or null when it sent none. */
    const absentSentence = (reason: string | null | undefined) =>
        (reason && block?.siteAnswersAbsentDetail?.[reason]) || null;
    // Strictly `=== false` / `=== true`. A row whose judgement is null is in
    // NEITHER bucket: it is a search we have not tested the site against, and
    // counting it as unanswered would inflate the problem with unmeasured rows.
    const counts = useMemo(() => ({
        unanswered: rows.filter((r) => r.siteAnswers === false).length,
        answered: rows.filter((r) => r.siteAnswers === true).length,
        all: rows.length,
    }), [rows]);
    // The searches the site judgement could not run for (ourteam-backend#5034):
    // `judged + sum(absent) === total`, so these are the rest of the 159 that the
    // two buckets above do not hold. Null when the field is absent (an older
    // payload), and the tab then does not render, as it did before.
    const unjudged = useMemo(() => {
        const absent = block?.siteAnswersCoverage?.absent;
        if (!absent) return null;
        return Object.values(absent).reduce((sum, n) => sum + (typeof n === "number" ? n : 0), 0);
    }, [block]);
    // Counted from the rows, not from the block's summary: the two must agree,
    // and the rows are what this table actually renders.
    const absentReasons = useMemo(() => {
        const by = new Map<string, number>();
        rows.forEach((r) => {
            if (r.siteAnswers !== null) return;
            const reason = r.siteAnswersAbsent || "unknown";
            by.set(reason, (by.get(reason) ?? 0) + 1);
        });
        // `forEach`, not spread over `.entries()`: this tsconfig targets ES5
        // for iterables and would need downlevelIteration.
        const out: Array<[string, number]> = [];
        by.forEach((count, reason) => out.push([reason, count]));
        return out.sort((a, b) => b[1] - a[1]);
    }, [rows]);
    const refresh = searchJob ? <CardRefresh job={searchJob} spec={SEARCH_REFRESH} /> : undefined;

    // Nothing to show, and the payload says why (#3897). The card keeps its
    // head and states the reason in place of the table; the filter does not
    // render at all, because there is nothing to filter.
    if (rows.length === 0) {
        // No search block on this scan and staff can pull it: say so, keep the
        // pull control. Otherwise a just-connected Search Console is a dead end.
        const notBuilt = !noSearchesReason && !!searchJob && searchesNotBuilt(block);
        if (!noSearchesReason && !notBuilt) return null;
        return (
            <FoldCard id="about-you" anchor="searches-about-you" defaultFolded={!notBuilt} title={TITLE} icon={ICON} right={refresh}>
                {searchJob && <RefreshProgress job={searchJob} runningText={SEARCH_REFRESH.running} />}
                <p className={cn(TYPE.tb, "m-0 text-gray-600")}>{noSearchesReason ?? SEARCHES_NOT_BUILT_SENTENCE}</p>
                <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-400")}>{footnote}</p>
            </FoldCard>
        );
    }

    // Opening on an empty bucket reads as "nothing here". When the site
    // judgement has not been computed for anything, the honest first view is
    // every search, with each row saying its own judgement is not measured.
    const effective: Filter =
        (counts.unanswered === 0 && filter === "unanswered") || (!unjudged && filter === "unjudged") ? "all" : filter;
    const shown = effective === "all"
        ? rows
        : rows.filter((r) => {
            if (effective === "answered") return r.siteAnswers === true;
            if (effective === "unjudged") return r.siteAnswers === null;
            return r.siteAnswers === false;
        });
    const anyFix = shown.some((r) => fixFor(r.coveredBy));

    return (
        <FoldCard
            id="about-you"
            anchor="searches-about-you"
            defaultFolded
            title={TITLE}
            icon={ICON}
            flush
            foldedFact={`${counts.unanswered} not answered on the site`}
            right={refresh}
        >
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 pb-3">
                <p className={cn(TYPE.tc, "m-0 min-w-0 flex-1 basis-64 text-gray-500")}>
                    Real searches from Google, matched to the 50 questions and to the pages that answer them.
                </p>
                <Seg<Filter>
                    size="sm"
                    ariaLabel="Show searches"
                    value={effective}
                    onChange={setFilter}
                    items={[
                        { key: "unanswered", label: "Your site does not answer", count: counts.unanswered },
                        { key: "answered", label: "Answered", count: counts.answered },
                        // Only when the backend says some were not judged: with none,
                        // the three numbers already add up and the tab stays off the screen.
                        ...(unjudged ? [{ key: "unjudged" as const, label: "Could not check", count: unjudged }] : []),
                        { key: "all", label: "All", count: counts.all },
                    ]}
                />
            </div>
            {searchJob && (
                <div className="px-5">
                    <RefreshProgress job={searchJob} runningText={SEARCH_REFRESH.running} />
                </div>
            )}
            <div className={TABLE.scroll}>
                <table className="w-full min-w-max border-collapse">
                    <thead>
                        <tr>
                            <th className={cn(TABLE.th, "text-left")}>Search</th>
                            <th className={cn(TABLE.th, "text-left")}>In your 50</th>
                            <th className={cn(TABLE.th, "w-24 text-right")}>Position</th>
                            <th className={cn(TABLE.th, "w-28 text-right")}>Searches</th>
                            <th className={cn(TABLE.th, "w-28 text-center")}>Site answers</th>
                            {anyFix && <th className={cn(TABLE.th, "w-24 text-center")}>Fix</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((r) => {
                            const fix = fixFor(r.coveredBy);
                            return (
                                <tr key={r.query} className="transition-colors hover:bg-gray-50">
                                    <td className={TABLE.td}>
                                        <span className="flex flex-wrap items-center gap-2 py-1.5">
                                            <span className={cn(TYPE.tb, "text-gray-900")}>&ldquo;{r.query}&rdquo;</span>
                                            <RankChip rank={r.rank} />
                                        </span>
                                    </td>
                                    <td className={cn(TABLE.td, TYPE.tc, "text-gray-500")}>
                                        {r.coveredBy !== null
                                            ? <span className="text-gray-700">Covered by Q{r.coveredBy}</span>
                                            : "Not in your 50"}
                                    </td>
                                    <td className={cn(TABLE.td, TYPE.tb, "text-right tabular-nums text-gray-700")}>
                                        #{Math.round(r.position)}
                                    </td>
                                    <td className={cn(TABLE.td, TYPE.tb, "text-right tabular-nums text-gray-700")}>{perYear(r.searches)}</td>
                                    <td className={cn(TABLE.td, "text-center")}>
                                        {r.siteAnswers === null ? (
                                            // The reason is the backend's sentence, verbatim, on
                                            // hover. A bare "Not measured" repeated 160 times is
                                            // the null this replaced, one step prettier.
                                            <span className={cn(TYPE.tm, "text-gray-400")} title={absentSentence(r.siteAnswersAbsent) ?? undefined}>
                                                Not measured
                                            </span>
                                        ) : (
                                            <Tag tone={r.siteAnswers ? "green" : "red"}>{r.siteAnswers ? "Yes" : "No"}</Tag>
                                        )}
                                    </td>
                                    {anyFix && (
                                        <td className={cn(TABLE.td, "text-center")}>
                                            {fix ? (
                                                <button
                                                    type="button"
                                                    onClick={() => openAction(fix)}
                                                    className={cn(TYPE.tc, "rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}
                                                >
                                                    View fix
                                                </button>
                                            ) : (
                                                <span className="text-gray-400">{EMPTY_CELL}</span>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <p className={cn(TYPE.tm, "m-0 border-t border-gray-100 px-5 py-2.5 text-gray-400")}>
                <span className="tabular-nums">{shown.length} of {counts.all}</span> · {footnote}
            </p>
            {/* Why the judgement is missing, once per reason, in the
                backend's own words (#5034). Without it a card of 160
                "Not measured" cells is a shrug: an operator cannot tell a
                scan that predates the check from a page that was never
                crawled, and those need opposite actions. That is an
                operator's question, in the backend's tool words, so the
                client does not get it. */}
            {isStaff && absentReasons.length > 0 && (
                <div className="border-t border-gray-100 px-5 py-2.5">
                    {absentReasons.map(([reason, count]) => (
                        <p key={reason} className={cn(TYPE.tm, "m-0 mt-1 text-gray-500 first:mt-0")}>
                            <span className="font-medium text-gray-700">{count} not judged</span>
                            {" · "}
                            {absentSentence(reason) ?? "No reason was given for this one."}
                        </p>
                    ))}
                </div>
            )}
        </FoldCard>
    );
}
